import { useEffect, useRef, useCallback, useState, useMemo, type MutableRefObject } from 'react';
import { ComponentTrackingConfig } from './types';
import { track } from '@eventbrite/legacy-infra.datalayer-library';

export const DEFAULT_INTERSECTION_OPTIONS: IntersectionObserverInit = {
  root: null,
  threshold: 0, // Trigger as soon as any part is visible for fastest response
  rootMargin: '0px 0px -150px 0px', // 150px above the conversion bar
};

/**
 * Finds the nearest vertical scroll container above an element, if any.
 *
 * IntersectionObserver's default root is the document viewport. When the
 * observed element lives inside an `overflow: auto` scroll container (the
 * listings `#listing-body`), the document root cannot see the element until
 * it physically enters the viewport — the ancestor's clip zeroes the
 * intersection rect. A widened `rootMargin` is therefore dead: it expands the
 * wrong viewport. Rooting the observer at the actual scroll container makes
 * the margin expand that container's fold, which is what lazy-load lead-time
 * margins need.
 */
const getNearestScrollableAncestor = (element: HTMLElement): Element | null => {
  let ancestor = element.parentElement;

  while (ancestor) {
    const { overflowY } = window.getComputedStyle(ancestor);
    const scrollsVertically =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      ancestor.scrollHeight > ancestor.clientHeight;

    if (scrollsVertically) {
      return ancestor;
    }

    ancestor = ancestor.parentElement;
  }

  return null;
};

/**
 * Resolves the observer options to use for an element.
 *
 * When the caller left `root` unset (the default document viewport) and the
 * element sits inside a vertical scroll container, the observer is rooted at
 * that container instead. An explicit `root` (including `root: document`) is
 * always respected.
 */
const resolveObserverOptions = (element: HTMLElement, options: IntersectionObserverInit): IntersectionObserverInit => {
  if (options.root !== null && options.root !== undefined) {
    return options;
  }

  const scrollRoot = getNearestScrollableAncestor(element);

  return scrollRoot ? { ...options, root: scrollRoot } : options;
};

/**
 * Pure visibility hook - detects when a component becomes visible using Intersection Observer.
 *
 * The returned ref is safe to attach immediately, even if the real DOM node appears later because
 * the component renders conditionally or another helper assigns `ref.current` after mount.
 *
 * Timeline:
 * 1. The hook returns a ref object.
 * 2. React (or a helper such as `assignRefsToNode`) assigns a DOM node to `ref.current`.
 * 3. The custom setter mirrors that node into React state.
 * 4. The effect reruns with the real node and starts observing it.
 * 5. The callback fires the first time that node enters the viewport.
 *
 * @param callback - Function to call when component becomes visible
 * @param options - Intersection Observer options
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibility = (
  callback: () => void,
  options: IntersectionObserverInit = DEFAULT_INTERSECTION_OPTIONS,
) => {
  const elementNodeRef = useRef<HTMLDivElement | null>(null);
  const [observedNode, setObservedNode] = useState<HTMLDivElement | null>(null);
  // hasBeenVisible is used to execute the callback only once
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const memoizedCallback = useCallback(callback, [callback]);
  const elementRef = useMemo(
    () =>
      ({
        get current() {
          return elementNodeRef.current;
        },
        set current(node: HTMLDivElement | null) {
          if (elementNodeRef.current === node) {
            return;
          }

          elementNodeRef.current = node;

          if (node) {
            setObservedNode(node);
          }
        },
      }) as MutableRefObject<HTMLDivElement | null>,
    [],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const element = observedNode;
    const resolvedOptions = element ? resolveObserverOptions(element, options) : options;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasBeenVisible) {
        memoizedCallback();
        setHasBeenVisible(true);
      }
    }, resolvedOptions);

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [memoizedCallback, hasBeenVisible, observedNode, options]);

  return elementRef;
};

/**
 * Enhanced visibility hook with tracking capabilities
 *
 * @param callback - Function to call when component becomes visible
 * @param trackingConfig - Optional tracking configuration for Heap
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibilityWithTracking = (callback?: () => void, trackingConfig?: ComponentTrackingConfig) => {
  const memoizedCallback = useCallback(() => {
    callback?.();

    if (trackingConfig) {
      dispatchTrackingEvents(trackingConfig);
    }
  }, [callback, trackingConfig]);

  const options = trackingConfig?.intersectionOptions || DEFAULT_INTERSECTION_OPTIONS;

  return useComponentVisibility(memoizedCallback, options);
};

export const dispatchTrackingEvents = (config: ComponentTrackingConfig) => {
  if (config.heap) {
    track({
      eventName: config.heap.eventName,
      eventData: {
        eventBucketLabel: config.heap.eventBucketLabel,
      },
    });
  }
};
